import React, { useState } from 'react';
import {
  ListItem,
  Typography,
  Button,
  Divider,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
  Box,
  Stack,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';

import UserFileSignatureChip from './UserFileSignatureChip';
import { downloadFileForPreview } from '../../../services/userFileService';
import PreviewPDFEdit from '../../PIIAnonymizerPage/PIIFileUploadPanel/PreviewPDFEdit/PreviewPDFEdit';
import PreviewWord from '../../PIIAnonymizerPage/PIIFileUploadPanel/PreviewWord';
import PreviewJSON from '../../PIIAnonymizerPage/PIIFileUploadPanel/PreviewJSON';

const UserFileRow = ({
  file,
  t,
  onDownload,
  onDelete,
  onOpenSignDialog,
  onOpenSignaturesDialog,
  withDivider = false,
}) => {
  const isOverdue = !!file.is_overdue;
  const hasDeadline = !!file.signature_deadline;
  const deadlineDate = hasDeadline ? new Date(file.signature_deadline) : null;

  const fileNameLower = (file.filename || '').toLowerCase();
  const isPdf = fileNameLower.endsWith('.pdf');
  const isDocx = fileNameLower.endsWith('.docx');
  const isJson = fileNameLower.endsWith('.json');
  const isPreviewable = isPdf || isDocx || isJson;

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFileItem, setPreviewFileItem] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewFileItem(null);
  };

  const handleOpenPreview = async () => {
    if (!isPreviewable) return;

    try {
      setPreviewLoading(true);

      const res = await downloadFileForPreview(file);
      if (!res?.success || !res.data?.blob) {
        window.alert(
          res?.error ||
            t(
              'files.preview_failed',
              'Nepodarilo sa načítať súbor na náhľad.'
            )
        );
        return;
      }

      setPreviewFileItem({
        blob: res.data.blob,
        name: file.filename,
        downloadName: file.filename,
      });
      setPreviewOpen(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  const renderOriginChip = () => {
    if (!file.origin) return null;

    let label = file.origin;
    let color = 'default';

    if (file.origin === 'external_anonymizer') {
      label = t('files.origin_external', 'Cenzor AI');
      color = 'info'; 
    }

    return (
      <Chip
        label={label}
        size="small"
        color={color}
        variant="outlined"
        sx={{ borderRadius: '6px', height: 22, fontSize: '0.7rem', fontWeight: 600, ml: 1.5 }}
      />
    );
  };

  const btnSx = {
    borderRadius: 2,
    fontWeight: 600,
    textTransform: 'none',
    px: 1.5,
    py: 0.5,
    lineHeight: 1.2,
  };

  return (
    <>
      <ListItem 
        disablePadding
        sx={{ 
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { xs: 'stretch', lg: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          p: 2,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 3,
          transition: 'all 0.2s ease',
          '&:hover': {
            borderColor: 'primary.main',
            bgcolor: 'action.hover', // Adapts perfectly to dark/light mode
          }
        }}
      >
        {/* Left Side: File Info */}
        <Stack direction="row" spacing={2} alignItems="center" sx={{ overflow: 'hidden' }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              width: 40, 
              height: 40, 
              borderRadius: 2, 
              bgcolor: 'action.selected', // Theme-aware subtle background
              color: 'text.secondary',
              flexShrink: 0
            }}
          >
            <InsertDriveFileOutlinedIcon />
          </Box>
          
          <Box sx={{ overflow: 'hidden' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', wordBreak: 'break-word' }}>
                {file.filename}
              </Typography>
              {renderOriginChip()}
            </Box>
            
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ rowGap: 0.5 }}>
              {file.uploaded_at && (
                <Typography variant="caption" color="text.secondary">
                  {t('files.uploaded_on', 'Nahraté dňa')}{' '}
                  {new Date(file.uploaded_at).toLocaleString()}
                </Typography>
              )}

              {hasDeadline && (
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: isOverdue ? 600 : 400,
                    color: isOverdue ? 'error.main' : 'text.secondary',
                  }}
                >
                  {t(
                    'files.signature_deadline',
                    'Termín podpisu: {{date}}',
                    { date: deadlineDate.toLocaleString() }
                  )}
                </Typography>
              )}
            </Stack>
          </Box>
        </Stack>

        {/* Right Side: Actions */}
        <Stack 
          direction="row" 
          spacing={1} 
          alignItems="center" 
          flexWrap="wrap"
          sx={{ rowGap: 1, justifyContent: { xs: 'flex-start', lg: 'flex-end' } }}
        >
          <Box sx={{ mr: { xs: 0, sm: 1 } }}>
            <UserFileSignatureChip
              t={t}
              signaturesCount={file.signatures_count}
              signedByMe={file.signed_by_me}
            />
          </Box>

          <Button
            variant="outlined"
            size="small"
            color="primary"
            sx={btnSx}
            onClick={() => onOpenSignaturesDialog(file)}
          >
            {t('files.view_signatures', 'Podpisy')}
          </Button>

          <Button
            variant="contained"
            size="small"
            color="primary"
            disableElevation
            sx={btnSx}
            disabled={isOverdue}
            onClick={() => onOpenSignDialog(file)}
          >
            {t('files.sign_action', 'Podpísať')}
          </Button>

          {/* Icon actions using native MUI colors for safe Dark/Light mode handling */}
          <Stack direction="row" spacing={0.5} alignItems="center">
            {isPreviewable && (
              <Tooltip title={t('files.preview', 'Náhľad')}>
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleOpenPreview}
                    disabled={previewLoading}
                  >
                    {previewLoading ? <CircularProgress size={20} color="inherit" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            )}

            <Tooltip title={t('files.download', 'Stiahnuť')}>
              <IconButton
                size="small"
                onClick={() => onDownload(file)}
                sx={{ color: 'text.secondary' }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title={t('files.delete', 'Zmazať')}>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete(file.id)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </ListItem>

      {/* Divider uses standard divider color now */}
      {withDivider && <Divider component="li" sx={{ my: 1 }} />}

      {/* Previews */}
      {isPdf && (
        <PreviewPDFEdit
          open={previewOpen && isPdf}
          fileItem={previewFileItem}
          onClose={handleClosePreview}
        />
      )}

      {isDocx && (
        <PreviewWord
          open={previewOpen && isDocx}
          fileItem={previewFileItem}
          onClose={handleClosePreview}
        />
      )}

      {isJson && (
        <PreviewJSON
          open={previewOpen && isJson}
          fileItem={previewFileItem}
          onClose={handleClosePreview}
        />
      )}
    </>
  );
};

export default UserFileRow;